import { ArrowBackOutlined } from '@mui/icons-material'
import {
  Alert,
  Button,
  ButtonGroup,
  Checkbox,
  IconButton,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { quoteStatuses } from 'src/lists'
import { itesArLineType, quoteLineType, sewpTrLineType } from 'src/types'
import { COLUMN_MAP, ColumnConfig } from 'src/EditQuote/lines/LinesColumns'
import { QuoteLinesProvider, useQuoteLines } from 'src/EditQuote/lines/QuoteLinesContext'
import { EditableCell } from 'src/EditQuote/steps/draft/EditableCell'
import 'src/EditQuote/lines/LinesShared.css'
import './TechnicalRequest.css'
import axios from 'axios'
import TrBulkEdit from './TrBulkEdit'

const COLUMN_WIDTHS = {
  CHECKBOX: '74px',
  NUMBER: '60px',
  CLIN: '170px',
  DESCRIPTION: '290px',
  CLIN_TYPE: '166px',
  DISCOUNT_CLASS: '270px',
  LIST_PRICE: '153px',
  SEWP_PRICE: '153px',
  DISCOUNT_PCT: '214px',
  EPEAT_FLAG: '271px',
  ENERGY_STAR_FLAG: '195px',
  TAA_FLAG: '187px',
  MFR_PART_NUMBER: '170px',
  CATALOG_NUMBER: '170px',
  MAJOR_END_ITEM: '170px',
  MEI_COMPONENT: '170px',
  MANUFACTURER: '170px',
  VENDOR_PART_NUMBER: '170px',
}

type TrColumn = {
  name: string
  header: string
  width: string
  sticky?: 'clin' | 'description'
  minWidth?: boolean
}

export const TR_COLUMNS: TrColumn[] = [
  {
    name: 'CLIN',
    header: 'CLIN\n(OEM + MFR Part #)',
    width: COLUMN_WIDTHS.CLIN,
    sticky: 'clin',
  },
  {
    name: 'Description',
    header: 'Description',
    width: COLUMN_WIDTHS.DESCRIPTION,
    sticky: 'description',
    minWidth: true,
  },
  {
    name: 'CLIN Type',
    header: 'CLINTYPE',
    width: COLUMN_WIDTHS.CLIN_TYPE,
  },
  {
    name: 'Discount Class',
    header: 'DISCOUNTCLASS',
    width: COLUMN_WIDTHS.DISCOUNT_CLASS,
  },
  {
    name: 'MSRP',
    header: 'LISTPRICE',
    width: COLUMN_WIDTHS.LIST_PRICE,
  },
  {
    name: 'Contract Ceiling Price',
    header: 'SEWPPRICE',
    width: COLUMN_WIDTHS.SEWP_PRICE,
  },
  {
    name: 'Discount PCT',
    header: 'DISCOUNTPCT',
    width: COLUMN_WIDTHS.DISCOUNT_PCT,
  },
  {
    name: 'EPEAT',
    header: 'EPEATFLAG',
    width: COLUMN_WIDTHS.EPEAT_FLAG,
  },
  {
    name: 'Energy Star',
    header: 'ENERGYSTARFLAG',
    width: COLUMN_WIDTHS.ENERGY_STAR_FLAG,
  },
  {
    name: 'TAA',
    header: 'TAAFLAG',
    width: COLUMN_WIDTHS.TAA_FLAG,
  },
]

export const ITES_AR_COLUMNS: TrColumn[] = [
  {
    name: 'Mfr Part Number',
    header: 'Mfr Part Number',
    width: COLUMN_WIDTHS.MFR_PART_NUMBER,
    sticky: 'clin',
  },
  {
    name: 'Description',
    header: 'Description',
    width: COLUMN_WIDTHS.DESCRIPTION,
    sticky: 'description',
  },
  {
    name: 'Catalog Number',
    header: 'Catalog Number',
    width: COLUMN_WIDTHS.CATALOG_NUMBER,
  },
  { name: 'Major End Item', header: 'Major End Item', width: COLUMN_WIDTHS.MAJOR_END_ITEM },
  { name: 'MEI Component', header: 'MEI Component', width: COLUMN_WIDTHS.MEI_COMPONENT },
  { name: 'MSRP', header: 'List Price', width: COLUMN_WIDTHS.LIST_PRICE },
  { name: 'Contract Ceiling Price', header: 'ITES-4H Sell Price', width: COLUMN_WIDTHS.SEWP_PRICE },
  { name: 'Percent Off List Price', header: 'Percent Off List Price', width: COLUMN_WIDTHS.DISCOUNT_PCT },
  { name: 'Manufacturer', header: 'Manufacturer', width: COLUMN_WIDTHS.MANUFACTURER },
  { name: 'Supplier Part Number', header: 'Vendor Part Number', width: COLUMN_WIDTHS.VENDOR_PART_NUMBER },
]

export const DISCOUNT_CLASSES = [
  { class: 'Computer System', subclass: 'Miscellaneous Computer System' },
  { class: 'Storage Device', subclass: 'Miscellaneous Storage Device' },
  { class: 'Software', subclass: ' Miscellaneous Software' },
  { class: 'Communication Devices', subclass: ' Miscellaneous Communication Devices' },
  { class: 'Power Related Technology', subclass: ' Miscellaneous Power Related Technology' },
  { class: 'Input/Output device', subclass: ' Miscellaneous Input/Output device' },
  { class: 'Networking Equipment', subclass: ' Miscellaneous Networking Equipment' },
  { class: 'Cabinets / enclosures', subclass: ' Miscellaneous Cabinets / enclosures' },
  { class: 'Data Acquisition Technology', subclass: ' Miscellaneous Data Acquisition Technology' },
  { class: 'Digital Image Tools', subclass: ' Miscellaneous Digital Image Tools' },
  { class: 'Video Conferencing Tools', subclass: ' Miscellaneous Video Conferencing Tools' },
  { class: 'Cables / Wiring Miscellaneous', subclass: ' Miscellaneous Cables / Wiring' },
  { class: 'Maintenance / Warranty', subclass: ' Miscellaneous Maintenance / Warranty' },
  { class: 'System Service Fee', subclass: ' Miscellaneous System Service Fee' },
  { class: 'Documentation', subclass: ' Miscellaneous Documentation' },
  { class: 'Training', subclass: ' Miscellaneous Training' },
  { class: 'Analysts', subclass: ' Miscellaneous Analysts' },
  { class: 'Installation', subclass: ' Miscellaneous Installation' },
]

export type preSubmitSewpTrLine = Partial<
  quoteLineType &
    Omit<
      sewpTrLineType,
      | 'SewpPrice'
      | 'ItemDescription'
      | 'Clin'
      | 'Provider'
      | 'PartNumber'
      | 'ListPrice'
      | 'EpeatFlag'
      | 'EnergyStarFlag'
      | 'TaaFlag'
      | 'BundleFlag'
      | 'ShortDescription'
    >
>

export type preSubmitItesArLine = Partial<
  quoteLineType & Omit<itesArLineType, 'TransactionType' | 'CatalogClin' | 'Unspsc'>
>

export const COLUMN_CONFIG_TR: Record<string, ColumnConfig<preSubmitSewpTrLine & preSubmitItesArLine>> = {
  ...COLUMN_MAP,
  'Discount PCT': {
    property: 'DiscountPercent',
    format: 'percentage',
  },
  'Discount Class': {
    property: 'DiscountClass',
    options: DISCOUNT_CLASSES.map(discountClass => ({ label: discountClass.class, value: discountClass.class })),
  },
  'CLIN Type': {
    property: 'ClinType',
    options: [
      { label: 'L | Labor', value: 'L' },
      { label: 'M | Maintenance', value: 'M' },
      { label: 'P | Product', value: 'P' },
      { label: 'S | Service', value: 'S' },
    ],
  },
  'List Price': {
    property: 'MSRP',
    format: 'currency',
  },
  'Catalog Number': {
    property: 'CatalogNumber',
    options: [
      { label: '1 - Servers', value: 1 },
      { label: '2 - Workstations, Thin Clients, Desktops and Notebooks', value: 2 },
      { label: '3 - Storage Systems', value: 3 },
      { label: '4 - Networking Equipment', value: 4 },
      { label: '5 - Imaging Equipment', value: 5 },
      { label: '6 - Cables, Connectors and Accessories', value: 6 },
      { label: '7 - Video Equipment Products', value: 7 },
      { label: '8 - Other', value: 8 },
    ],
  },
  'Major End Item': {
    property: 'MajorEndItem',
    options: [
      'Audio',
      'Audio Visual',
      'Business and Office Software',
      'Business Process Re-Engineering',
      'Carrying Case',
      'Communications Software',
      'Database Software',
      'Desktops',
      'Development',
      'Docking Station/Port Replicator',
      'DoD Information Network Approved Products List',
      'DoD Unified Capabilities Approved Products List',
      'Education/Training',
      'Electronic Product Environmental Assessment Tool',
      'Enterprise Design, Integration and Consolidation',
      'Entertainment / Education Software',
      'Extended Warranty/Maintenance',
      'Financial Software',
      'Graphics Software',
      'Handheld Devices',
      'Hard Drive',
      'Independent Verification and Validation (IV&V)',
      'Information Assurance (IA)',
      'Information Systems Security',
      'Information Technology Services',
      'Installation Services',
      'Internet / Web Software',
      'Internet Protocol Version 6 (IPv6)',
      'IT Services',
      'IT Utility and Security Software',
      'Licenses (Enterprise)',
      'Maintenance',
      'Maintenance Services',
      'Memory',
      'Migration/Integration IT Services',
      'Mobile Devices',
      'Modeling and Simulation Software',
      'Monitors',
      'Multi-Functional Devices',
      'Multimedia and Design Software',
      'N/A',
      'Network Support',
      'Networking Cables, Connectors, and Accessories',
      'Networking Products',
      'Notebooks',
      'Office Automation',
      'Operating Systems',
      'Optical Drive',
      'Optional Feeder Tray',
      'Peripherals',
      'Portable and Handheld Computers',
      'Power Supply',
      'Printers',
      'Processor/CPU',
      'Program and Development Software',
      'Program/Project Management',
      'Repair Services',
      'Ruggedized Devices',
      'Servers / Workstations',
      'Software Maintenance',
      'Storage and Network Storage',
      'Supplies',
      'Systems Operation and Maintenance',
      'Teleconferencing',
      'Thin Client',
      'Toner Cartridge',
      'Training',
      'Training / Reference Software',
      'Training Services',
      'Video Card',
      'VoIP/EoIP Products',
      'Warranty and Maintenance',
      'Workstations',
    ].map(majorEndItem => ({ label: majorEndItem, value: majorEndItem })),
  },
  'MEI Component': {
    property: 'MeiComponent',
    options: ['Hardware', 'Maintenance', 'Service', 'Software'].map(meiComponent => ({
      label: meiComponent,
      value: meiComponent,
    })),
  },
  'Mfr Part Number': {
    property: 'MfrPartNumber',
    nonEdit: true,
  },
  Description: {
    property: 'Description',
    nonEdit: true,
  },
  'Percent Off List Price': {
    property: 'PercentOffListPrice',
    format: 'percentage',
  },
}

export function updatesForColumn<T extends preSubmitSewpTrLine & preSubmitItesArLine>(
  line: T,
  column: string,
): Partial<T> {
  let changes: Partial<T> = {}
  if (column === 'Contract Ceiling Price') {
    const discount = 1 - (line.ContractCeilingPrice ?? 0) / (line.MSRP ?? 1)
    changes = (
      'DiscountPercent' in line ? { DiscountPercent: discount } : { PercentOffListPrice: discount }
    ) as Partial<T>
  } else if (column === 'MSRP') {
    changes = { ContractCeilingPrice: (line.MSRP ?? 0) * (1 - (line.DiscountPercent ?? 0)) } as Partial<T>
  } else if (column === 'Discount PCT') {
    changes = { ContractCeilingPrice: (line.MSRP ?? 0) * (1 - (line.DiscountPercent ?? 0)) } as Partial<T>
  } else if (column === 'Percent Off List Price') {
    changes = { ContractCeilingPrice: (line.MSRP ?? 0) * (1 - (line.PercentOffListPrice ?? 0)) } as Partial<T>
  } else if (column === 'Discount Class') {
    changes = {
      DiscountSubClass:
        DISCOUNT_CLASSES.find(discountClass => discountClass.class === line.DiscountClass)?.subclass ?? '',
    } as Partial<T>
  } else if (column === 'Manufacturer' && line.Manufacturer && typeof line.Manufacturer === 'object') {
    changes = { Manufacturer: (line.Manufacturer as { Manufacturer?: string }).Manufacturer } as Partial<T>
  } else if (column === 'Catalog Number') {
    changes = {
      PercentOffListPrice: [1, 3, 4, 6, 7].includes(line.CatalogNumber ?? 0) ? 0.2 : 0.15,
    } as Partial<T>
  }

  if (column === 'Contract Ceiling Price' || !('PercentOffListPrice' in changes || 'DiscountPercent' in changes))
    return changes
  return {
    ...changes,
    ...updatesForColumn(
      { ...line, ...changes },
      'PercentOffListPrice' in changes ? 'Percent Off List Price' : 'Discount PCT',
    ),
  }
}

function columnAlign(columnName: string): 'left' | 'right' {
  const format = COLUMN_CONFIG_TR[columnName].format
  return format === 'currency' || format === 'percentage' || (format === 'number' && columnName !== '#')
    ? 'right'
    : 'left'
}

function TechnicalRequestBody<T extends preSubmitSewpTrLine & preSubmitItesArLine>() {
  const navigate = useNavigate()
  const { quoteId } = useParams()
  const { quote } = useQuoteLines()

  const [lines, setLines] = useState<T[]>([])
  const [selectedLineItems, setSelectedLineItems] = useState<T[]>([])
  const [lineItemsLoading, setLineItemsLoading] = useState<boolean>(true)
  const [missingFields, setMissingFields] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [bulkEditOpen, setBulkEditOpen] = useState<boolean>(false)
  const originalLines = useRef<T[]>([])

  const isItes = useMemo(() => quote.ContractVehicle === 'ITES-4H', [quote.ContractVehicle])
  const columns = useMemo(() => (isItes ? ITES_AR_COLUMNS : TR_COLUMNS), [isItes])
  const tableMinWidth = useMemo(
    () =>
      (isItes ? ITES_AR_COLUMNS.map(column => column.width) : TR_COLUMNS.map(column => column.width)).reduce(
        (acc, curr) => acc + parseInt(curr),
        0,
      ),
    [isItes],
  )

  const allSelected = useMemo(
    () => lines.length > 0 && lines.every(line => selectedLineItems.some(s => s.LineItemId === line.LineItemId)),
    [lines, selectedLineItems],
  )
  const someSelected = useMemo(() => selectedLineItems.length > 0 && !allSelected, [selectedLineItems, allSelected])

  useEffect(() => {
    axios
      .get(`/quote/${quoteId}/sewpTr`)
      .then(response => {
        const sortedLines = flatLineItems(response.data)
        setLines(sortedLines)
        originalLines.current = sortedLines
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLineItemsLoading(false)
      })
  }, [])

  useEffect(() => {
    setSelectedLineItems(lines.filter(line => selectedLineItems.some(s => s.LineItemId === line.LineItemId)))
  }, [lines])

  const flatLineItems = (sortedLineItems: (T & { children: T[] })[]) =>
    sortedLineItems.flatMap(item => (item.LineNumber === -1 ? item.children : [item]))

  const handleResetLines = () => {
    setLines(originalLines.current)
    setSelectedLineItems([])
  }

  const handleSubmit = () => {
    setSubmitLoading(true)
    setSubmitError(null)
    let missingFieldsLocal = false
    const submitLines: Partial<sewpTrLineType>[] | Partial<itesArLineType>[] = []
    for (const line of lines) {
      if (isItes) {
        const itesLine = line as preSubmitItesArLine
        if (
          !itesLine.CatalogNumber ||
          !itesLine.MajorEndItem ||
          !itesLine.MeiComponent ||
          !itesLine.Manufacturer ||
          !itesLine.VendorPartNumber ||
          !itesLine.MSRP ||
          !itesLine.ContractCeilingPrice ||
          !itesLine.PercentOffListPrice
        ) {
          console.log(itesLine)
          missingFieldsLocal = true
          break
        } else {
          submitLines.push({
            QuoteId: quoteId,
            MfrPartNumber: itesLine.MfrPartNumber,
            Description: itesLine.Description,
            ContractNumber: quote.ContractNumber?.toUpperCase() ?? '',
            CatalogNumber: itesLine.CatalogNumber,
            MajorEndItem: itesLine.MajorEndItem,
            MeiComponent: itesLine.MeiComponent,
            Manufacturer: itesLine.Manufacturer,
            VendorPartNumber: `${itesLine.OEMCode}-${itesLine.VendorPartNumber}-${itesLine.CatalogNumber}`,
            ListPrice: itesLine.MSRP,
            Ites4hSellPrice: itesLine.ContractCeilingPrice,
            PercentOffListPrice: itesLine.PercentOffListPrice,
            CatalogClin:
              itesLine.CatalogNumber.toString().padStart(2, '0') +
              (
                (COLUMN_CONFIG_TR['MEI Component'].options
                  ?.map(option => option.value)
                  .indexOf(itesLine.MeiComponent) ?? -1) + 1
              )
                .toString()
                .padStart(2, '0'),
            Unspsc: itesLine.UNSPSC ?? undefined,
          })
        }
      } else {
        const sewpLine = line as preSubmitSewpTrLine
        if (
          !sewpLine.Description ||
          !sewpLine.ClinType ||
          !sewpLine.OEMCode ||
          !sewpLine.MfrPartNumber ||
          !sewpLine.DiscountClass ||
          !sewpLine.DiscountSubClass ||
          !sewpLine.MSRP ||
          !sewpLine.ContractCeilingPrice ||
          !sewpLine.DiscountPercent ||
          !sewpLine.EPEAT ||
          !sewpLine.EnergyStar ||
          !sewpLine.TAA
        ) {
          missingFieldsLocal = true
          break
        } else {
          submitLines.push({
            QuoteId: quoteId,
            ContractNumber: quote.ContractNumber?.toLowerCase() ?? '',
            ItemDescription: sewpLine.Description,
            Clin: sewpLine.OEMCode + sewpLine.MfrPartNumber,
            ClinType: sewpLine.ClinType,
            Provider: sewpLine.OEMCode,
            PartNumber: sewpLine.MfrPartNumber,
            DiscountClass: sewpLine.DiscountClass,
            DiscountSubClass: sewpLine.DiscountSubClass,
            ListPrice: sewpLine.MSRP,
            SewpPrice: sewpLine.ContractCeilingPrice,
            DiscountPercent: sewpLine.DiscountPercent,
            EpeatFlag: sewpLine.EPEAT,
            EnergyStarFlag: sewpLine.EnergyStar,
            TaaFlag: sewpLine.TAA,
            BundleFlag: !!sewpLine.Bundle && sewpLine.LineNumber !== -1,
            ShortDescription: sewpLine.Description.substring(0, 80),
          })
        }
      }
    }

    if (missingFieldsLocal) {
      setMissingFields(true)
      setSubmitLoading(false)
    } else {
      axios
        .post(`/catalog/${quote.ContractNumber}/tr`, { trLines: submitLines })
        .then(() => {
          navigate(`/quote/${quoteId}`)
        })
        .catch(error => {
          console.error(error)
          setSubmitError(error.response?.data?.message ?? 'Failed to submit technical request')
        })
        .finally(() => {
          setSubmitLoading(false)
        })
    }
  }

  return (
    <div className="technical-request">
      <div className="tr-header">
        <div>
          <IconButton
            aria-label="Back to validate"
            onClick={() =>
              navigate(`/quote/${quoteId}`, {
                state: { activeStep: quoteStatuses.indexOf('Validate') },
              })
            }
          >
            <ArrowBackOutlined />
          </IconButton>
          <Typography variant="h6">
            {quote.ContractVehicle === 'ITES-4H' ? 'Approval Request' : 'Technical Request'}
          </Typography>
        </div>
        <div>
          <Alert severity="info">All fields are required before Compliance Review.</Alert>
          <ButtonGroup variant="contained">
            <Button onClick={() => setBulkEditOpen(true)} disabled={selectedLineItems.length === 0}>
              Bulk Edit
            </Button>
            <Button onClick={handleResetLines}>Reset</Button>
            <Button onClick={handleSubmit} loading={submitLoading}>
              Submit Request
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <div className="lines-view technical-request-lines">
        <TableContainer className="lines-table-container">
          <Table stickyHeader sx={{ minWidth: `${tableMinWidth}px` }}>
            <TableHead>
              <TableRow>
                <TableCell className="lines-sticky-col tr-sticky-checkbox" width={COLUMN_WIDTHS.CHECKBOX}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => {
                      if (allSelected) {
                        setSelectedLineItems([])
                      } else {
                        setSelectedLineItems(lines)
                      }
                    }}
                  />
                </TableCell>
                <TableCell className="tr-sticky-number" width={COLUMN_WIDTHS.NUMBER}>
                  #
                </TableCell>
                {columns.map(column => (
                  <TableCell
                    key={column.name}
                    className={
                      column.sticky === 'clin'
                        ? 'tr-sticky-clin'
                        : column.sticky === 'description'
                        ? 'tr-sticky-description'
                        : undefined
                    }
                    width={column.minWidth ? undefined : column.width}
                    sx={column.minWidth ? { minWidth: column.width } : undefined}
                    align={columnAlign(column.name)}
                  >
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {lineItemsLoading ? (
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from({ length: TR_COLUMNS.length + 2 }).map((__, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                {lines.map(item => {
                  return (
                    <TableRow key={item.LineItemId} className="no-bundle">
                      <TableCell className="lines-sticky-col tr-sticky-checkbox" width={COLUMN_WIDTHS.CHECKBOX}>
                        <Checkbox
                          checked={selectedLineItems.some(s => s.LineItemId === item.LineItemId)}
                          onChange={() => {
                            setSelectedLineItems(prev =>
                              prev.some(s => s.LineItemId === item.LineItemId)
                                ? prev.filter(s => s.LineItemId !== item.LineItemId)
                                : [...prev, item],
                            )
                          }}
                        />
                      </TableCell>
                      <TableCell className="tr-sticky-number" width={COLUMN_WIDTHS.NUMBER}>
                        <Typography variant="body2">{item.LineNumber}</Typography>
                      </TableCell>
                      {columns.map(column => {
                        const columnConfig = COLUMN_CONFIG_TR[column.name]
                        return (
                          <TableCell
                            key={column.name}
                            className={
                              column.sticky === 'clin'
                                ? 'tr-sticky-clin'
                                : column.sticky === 'description'
                                ? 'tr-sticky-description'
                                : undefined
                            }
                            align={columnAlign(column.name)}
                          >
                            <EditableCell
                              value={columnConfig.compute ? columnConfig.compute(item) : item[columnConfig.property]}
                              canEdit={!columnConfig.nonEdit}
                              inputType={columnConfig.format}
                              source={columnConfig.masterList}
                              options={columnConfig.options}
                              onChange={value => {
                                setLines(prev =>
                                  prev.map(line =>
                                    line.LineItemId === item.LineItemId && 'property' in columnConfig
                                      ? {
                                          ...line,
                                          [columnConfig.property]: value,
                                          ...updatesForColumn({ ...line, [columnConfig.property]: value }, column.name),
                                        }
                                      : line,
                                  ),
                                )
                              }}
                            />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )
                })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>

      <TrBulkEdit
        open={bulkEditOpen}
        selectedLineItems={selectedLineItems}
        contractVehicle={quote.ContractVehicle ?? ''}
        contractNumber={quote.ContractNumber}
        setLineItems={setLines}
        onClose={() => setBulkEditOpen(false)}
      />
      <Snackbar open={missingFields} onClose={() => setMissingFields(false)}>
        <Alert severity="error" variant="filled">
          Please fill in all required fields
        </Alert>
      </Snackbar>
      <Snackbar open={submitError !== null} onClose={() => setSubmitError(null)}>
        <Alert severity="error" variant="filled">
          {submitError}
        </Alert>
      </Snackbar>
    </div>
  )
}

export default function TechnicalRequest() {
  return (
    <QuoteLinesProvider>
      <TechnicalRequestBody />
    </QuoteLinesProvider>
  )
}
